import {
    TaskColumn,
    TaskSortValue,
    TaskView,
} from "@common/types/views/tasksView";
import { NamedView, ViewDensity, ViewType } from "@common/types/views/views";
import { TaskGroupBy, TaskStatus } from "@common/types/task";
import { useUserStatus } from "@hooks/useUserStatus";
import { useMemo } from "react";
import { DEFAULT_VIEW_ID } from "@components/views/constants";
import {
    buildAssignedToMeView,
    buildClientRequestsView,
    buildMonthEndCloseView,
    buildOrganizationTaskViews,
    buildUnassignedView,
    buildWaitingOnClientView,
    ClientTaskViewOptions,
} from "./OrganizationTaskViews";
import {
    isCloseStageColumnShown,
    isMonthEndCloseViewShown,
    MonthEndCloseViewParams,
} from "./monthEndCloseView";
import { useMonthEndCloseViewParams } from "./useMonthEndCloseViewParams";

export function buildDefaultTaskColumns({
    organizationId,
    hasCloseStages,
    hasCloses,
}: MonthEndCloseViewParams): TaskView["columns"] {
    return [
        { id: TaskColumn.NAME, options: { pin: "left" } },
        { id: TaskColumn.DUE_DATE },
        { id: TaskColumn.PRIORITY },
        { id: TaskColumn.ENTITIES },
        { id: TaskColumn.ASSIGNEE },
        ...(isCloseStageColumnShown({ organizationId, hasCloseStages })
            ? [{ id: TaskColumn.CLOSE_STAGE }]
            : []),
        ...(organizationId && hasCloses
            ? [{ id: TaskColumn.CLOSE }, { id: TaskColumn.SERVICE_PERIOD }]
            : []),
        { id: TaskColumn.ACTIONS, options: { pin: "right" } },
    ];
}

interface DefaultTaskViewParams extends MonthEndCloseViewParams {
    defaultStatuses: TaskStatus[] | undefined;
}

/** All tasks: the catch-all view, listed last on both Tasks pages. Its id stays `default` so cached picks still match. */
export function buildDefaultTaskView({
    defaultStatuses,
    ...columnParams
}: DefaultTaskViewParams): NamedView<TaskView> {
    return {
        id: DEFAULT_VIEW_ID,
        name: "All tasks",
        type: ViewType.TASK,
        columns: buildDefaultTaskColumns(columnParams),
        filters: {
            taskSort: TaskSortValue.CREATED_AT_DESC,
            ...(defaultStatuses && { taskStatuses: defaultStatuses }),
        },
        options: {
            selection: true,
            density: ViewDensity.MD,
            groupBy: TaskGroupBy.STATUS,
        },
    };
}

export interface BuiltInTaskViewsParams extends DefaultTaskViewParams {
    /** The current user; views scoped to them are left out until it loads. */
    userId: number | undefined;
}

export type BuildBuiltInTaskViews = (
    params: BuiltInTaskViewsParams,
) => Array<NamedView<TaskView>>;

interface WorkspacePageTaskViewsOptions {
    /** Only firm members in a firm-managed workspace get the client views. */
    hasClientTaskViews: boolean;
}

const WORKSPACE_CLIENT_TASK_VIEW_OPTIONS: ClientTaskViewOptions = {
    groupBy: TaskGroupBy.DUE_DATE,
    sort: TaskSortValue.DUE_DATE_ASC,
};

/** The workspace Tasks page's built-in views, in picker order. */
const buildWorkspacePageTaskViews = ({
    userId,
    hasClientTaskViews,
    ...defaultViewParams
}: BuiltInTaskViewsParams & WorkspacePageTaskViewsOptions): Array<
    NamedView<TaskView>
> => {
    const isCloseStageShown = isCloseStageColumnShown(defaultViewParams);
    const isMonthEndCloseShown = isMonthEndCloseViewShown(defaultViewParams);

    return [
        ...(isMonthEndCloseShown ? [buildMonthEndCloseView()] : []),
        ...(userId === undefined
            ? []
            : [buildAssignedToMeView(userId, { isCloseStageShown })]),
        ...(hasClientTaskViews
            ? [
                  buildUnassignedView(),
                  buildWaitingOnClientView(WORKSPACE_CLIENT_TASK_VIEW_OPTIONS),
                  buildClientRequestsView(WORKSPACE_CLIENT_TASK_VIEW_OPTIONS),
              ]
            : []),
        buildDefaultTaskView(defaultViewParams),
    ];
};

/** The workspace Tasks page's views builder for the current user; memoize it so the views aren't rebuilt every render. */
export const createWorkspacePageTaskViewsBuilder =
    (options: WorkspacePageTaskViewsOptions): BuildBuiltInTaskViews =>
    (params) =>
        buildWorkspacePageTaskViews({ ...params, ...options });

/** The organization Tasks page's built-in views, in picker order. */
export const buildOrganizationPageTaskViews: BuildBuiltInTaskViews = ({
    userId,
    ...defaultViewParams
}) => [
    ...buildOrganizationTaskViews(userId),
    buildDefaultTaskView(defaultViewParams),
];

interface UseBuiltInTaskViewsParams {
    defaultStatuses: TaskStatus[] | undefined;
    /** Which page's views to build: from `createWorkspacePageTaskViewsBuilder` or `buildOrganizationPageTaskViews`. */
    buildViews: BuildBuiltInTaskViews;
}

interface BuiltInTaskViewsState {
    builtInViews: Array<NamedView<TaskView>>;
    /** The user, closes or close stages are loading, so views built from them may still be missing. */
    isLoadingBuiltInViews: boolean;
}

/** Gathers the scope, closes and current user, and builds the page's built-in views from them. */
export function useBuiltInTaskViews({
    defaultStatuses,
    buildViews,
}: UseBuiltInTaskViewsParams): BuiltInTaskViewsState {
    const {
        organizationId,
        hasCloseStages,
        hasCloses,
        isLoading: isMonthEndCloseLoading,
    } = useMonthEndCloseViewParams();
    const { data: userStatus, isLoading: isUserStatusLoading } =
        useUserStatus();
    const userId = userStatus?.user.id;

    const isLoadingBuiltInViews = isMonthEndCloseLoading || isUserStatusLoading;

    const builtInViews = useMemo(
        () =>
            buildViews({
                organizationId,
                hasCloseStages,
                hasCloses,
                defaultStatuses,
                userId,
            }),
        [
            buildViews,
            organizationId,
            hasCloseStages,
            hasCloses,
            defaultStatuses,
            userId,
        ],
    );

    return useMemo(
        () => ({ builtInViews, isLoadingBuiltInViews }),
        [builtInViews, isLoadingBuiltInViews],
    );
}
